import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ClipboardCheck, FileSearch, ShieldCheck, Loader2, Download } from "lucide-react";

const human = (s) => (s || "").replace(/_/g, " ");

export default function VerificationOps() {
  const [queue, setQueue] = useState(null);
  const [template, setTemplate] = useState(null);
  const [active, setActive] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [checks, setChecks] = useState({});
  const [triage, setTriage] = useState({ outcome: "accepted_for_analysis", notes: "" });
  const [finding, setFinding] = useState({ classification: "consistent_with_available_evidence", analyst_summary: "" });
  const [review, setReview] = useState({ decision: "approved", reviewer_comments: "" });
  const [note, setNote] = useState({ entry_type: "note", summary: "", source: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [q, t] = await Promise.all([api.get("/verification/queue"), api.get("/verification/checklist-template")]);
    setQueue(q.data);
    setTemplate(t.data);
  };
  useEffect(() => { load().catch(() => setMsg("Analyst or supervisor access is required.")); }, []);

  const open = async (c) => {
    setActive(c);
    setChecks({});
    setMsg("");
    const { data } = await api.get(`/verification/cases/${c.id}/evidence`);
    setEvidence(data);
  };

  const run = async (label, fn) => {
    setBusy(label);
    try { const r = await fn(); setMsg(r || "Done."); await load(); if (active) await open(active); }
    catch (e) { setMsg(e?.response?.data?.detail || "That action could not be completed."); }
    setBusy("");
  };

  const documents = async () => {
    const { data } = await api.get(`/verification/cases/${active.id}/documents`);
    for (const d of data.documents) {
      const res = await api.get(`/files/${d.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    }
    if (!data.documents.length) setMsg("No documents have been uploaded to this case.");
  };

  if (!queue) return <p className="p-6 text-sm text-amber-800" data-testid="verification-ops-message">{msg || "Loading…"}</p>;

  return (
    <div data-testid="verification-ops">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2"><FileSearch className="w-4 h-4" />Verification queue ({queue.count})</h2>
      </div>
      <p className="mt-2 text-xs text-slate-500">{queue.note}</p>

      <div className="mt-4 grid lg:grid-cols-[320px_1fr] gap-6 items-start">
        <ul className="space-y-2" data-testid="verification-queue-list">
          {queue.cases.map((c) => (
            <li key={c.id}>
              <button data-testid={`queue-case-${c.id}`} onClick={() => open(c)}
                className={`w-full text-left card p-4 ${active?.id === c.id ? "border-[#1e3a8a]" : ""}`}>
                <p className="text-sm font-semibold">{c.case_number}</p>
                <p className="text-xs text-slate-500">{c.scope_name}</p>
                <p className="text-xs mt-1"><span className="pill bg-slate-100 text-slate-700">{human(c.status)}</span> · {c.documents} document(s)</p>
                {c.assigned_analyst_id && <p className="text-[11px] text-slate-500 mt-1">Assigned</p>}
              </button>
            </li>
          ))}
          {queue.cases.length === 0 && <li className="text-sm text-slate-500" data-testid="queue-empty">No paid cases are awaiting analysis.</li>}
        </ul>

        {active && (
          <div className="space-y-5">
            <section className="card p-5">
              <div className="flex flex-wrap gap-2">
                <button data-testid="assign-case-button" onClick={() => run("assign", async () => {
                  const { data } = await api.post(`/verification/cases/${active.id}/assign`);
                  return `Assigned to ${data.assigned_to}.`;
                })} className="btn btn-ghost">Assign to me</button>
                <button data-testid="open-documents-button" onClick={() => run("docs", documents)} className="btn btn-ghost">
                  <Download className="w-4 h-4 mr-1" />Open documents
                </button>
              </div>
            </section>

            <section className="card p-5">
              <h3 className="font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4" />Triage</h3>
              <select data-testid="triage-outcome-select" className="field mt-3" value={triage.outcome}
                onChange={(e) => setTriage({ ...triage, outcome: e.target.value })}>
                {template.triage_outcomes.map((o) => <option key={o} value={o}>{human(o)}</option>)}
              </select>
              <textarea data-testid="triage-notes-input" rows={2} className="field mt-2" placeholder="Triage notes (required)"
                value={triage.notes} onChange={(e) => setTriage({ ...triage, notes: e.target.value })} />
              <button data-testid="submit-triage-button" onClick={() => run("triage", async () => {
                const { data } = await api.post(`/verification/cases/${active.id}/triage`, triage);
                return `Triage recorded. Case status: ${human(data.status)}.`;
              })} className="btn btn-primary mt-3">Record triage</button>
            </section>

            <section className="card p-5">
              <h3 className="font-semibold">Analyst checklist</h3>
              <ul className="mt-3 space-y-2">
                {template.items.map((i) => (
                  <li key={i.key} className="flex gap-2 items-start text-sm">
                    <input data-testid={`checklist-${i.key}`} type="checkbox" checked={checks[i.key] === true || checks[i.key] === false ? true : false}
                      onChange={(e) => setChecks(e.target.checked ? { ...checks, [i.key]: true } : Object.fromEntries(Object.entries(checks).filter(([k]) => k !== i.key)))} />
                    <span>
                      {i.description}
                      {i.key in checks && (
                        <button data-testid={`checklist-toggle-${i.key}`} onClick={() => setChecks({ ...checks, [i.key]: !checks[i.key] })}
                          className={`ml-2 pill ${checks[i.key] ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
                          {checks[i.key] ? "Satisfied" : "Not satisfied"}
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <button data-testid="save-checklist-button" onClick={() => run("checklist", async () => {
                const { data } = await api.put(`/verification/cases/${active.id}/checklist`, { results: checks, notes: {} });
                return data.complete ? "Checklist complete." : `Outstanding items: ${data.outstanding.length}.`;
              })} className="btn btn-primary mt-3">Save checklist</button>
            </section>

            <section className="card p-5">
              <h3 className="font-semibold">Evidence log</h3>
              <ul className="mt-3 space-y-2 text-sm" data-testid="evidence-entries">
                {(evidence?.entries || []).map((e) => (
                  <li key={e.id} className="border-l-2 border-slate-200 pl-3">
                    <p className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString("en-GB")} · {human(e.entry_type)} · {e.actor_email}</p>
                    <p>{e.detail?.summary || e.detail?.notes || JSON.stringify(e.detail)}</p>
                  </li>
                ))}
              </ul>
              <input data-testid="evidence-summary-input" className="field mt-3" placeholder="Evidence summary"
                value={note.summary} onChange={(e) => setNote({ ...note, summary: e.target.value })} />
              <input data-testid="evidence-source-input" className="field mt-2" placeholder="Source (organisation, email, telephone)"
                value={note.source} onChange={(e) => setNote({ ...note, source: e.target.value })} />
              <button data-testid="add-evidence-button" onClick={() => run("evidence", async () => {
                await api.post(`/verification/cases/${active.id}/evidence`, note);
                setNote({ entry_type: "note", summary: "", source: "" });
                return "Evidence recorded. The log is append-only.";
              })} className="btn btn-ghost mt-3">Add evidence entry</button>
            </section>

            <section className="card p-5">
              <h3 className="font-semibold">Submit finding</h3>
              <select data-testid="finding-classification-select" className="field mt-3" value={finding.classification}
                onChange={(e) => setFinding({ ...finding, classification: e.target.value })}>
                {template.classifications.map((c) => <option key={c} value={c}>{human(c)}</option>)}
              </select>
              <textarea data-testid="finding-summary-input" rows={4} className="field mt-2"
                placeholder="Explain the reasoning and the evidence relied upon (minimum 40 characters)"
                value={finding.analyst_summary} onChange={(e) => setFinding({ ...finding, analyst_summary: e.target.value })} />
              <button data-testid="submit-finding-button" onClick={() => run("finding", async () => {
                const { data } = await api.post(`/verification/cases/${active.id}/finding`, finding);
                return data.note;
              })} className="btn btn-primary mt-3">Submit for second review</button>
            </section>

            <section className="card p-5">
              <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Second-person review (supervisor)</h3>
              <select data-testid="review-decision-select" className="field mt-3" value={review.decision}
                onChange={(e) => setReview({ ...review, decision: e.target.value })}>
                <option value="approved">Approve and issue report</option>
                <option value="returned_to_analyst">Return to analyst</option>
              </select>
              <textarea data-testid="review-comments-input" rows={3} className="field mt-2" placeholder="Reviewer comments (required)"
                value={review.reviewer_comments} onChange={(e) => setReview({ ...review, reviewer_comments: e.target.value })} />
              <button data-testid="submit-review-button" onClick={() => run("review", async () => {
                const { data } = await api.post(`/verification/cases/${active.id}/second-review`, review);
                return data.reference ? `Report ${data.reference} issued. Validation code ${data.validation_code}.` : "Returned to the analyst.";
              })} className="btn btn-primary mt-3">
                {busy === "review" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Record review
              </button>
              <p className="mt-2 text-xs text-slate-500">A report can only be issued by a supervisor who is not the analyst who submitted the finding.</p>
            </section>
          </div>
        )}
      </div>
      {msg && <p className="mt-4 text-sm text-slate-800" data-testid="verification-ops-message">{msg}</p>}
    </div>
  );
}
